import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors} from '@angular/forms';
import {BaseComponent, TemplateFeature} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'

import {NbButtonModule, NbInputModule, NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';
import {TuiIcon} from '@taiga-ui/core'

interface Link {
  header?: string;
  url: string;
}

@Component({
  selector: 'app-tf-link',
  templateUrl: './tf-link.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbButtonModule, NbTooltipModule, FormsModule, ReactiveFormsModule, NbInputModule, RouterLink, TuiIcon]
})
export class TfLinkComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;
  @Input() showRemoveButton = false;
  @Output() removeEvent = new EventEmitter<void>();

  form = new UntypedFormGroup({
    header: new UntypedFormControl(null),
    url: new UntypedFormControl(null, this.featureLinkValidator),
  });

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe((data: Link) => {
        const featureData: string = this.isNotEmpty(data) ? JSON.stringify(this.parseLinkData(data)) : null;
        this.setFeature(featureData, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel && featureModel[this.feature.id]) {
      const link: Link = JSON.parse(featureModel[this.feature.id].toString());

      if (link && this.isNotEmpty(link)) {
        this.form.patchValue(this.parseLinkData(link), {emitEvent: false});
        this.setDefaultFeatureValue(JSON.stringify(this.form.value), this.form.valid, this.isShowEmptyWarning());
        return;
      }
    }

    this.setDefaultFeatureValue(null, true, this.isRequired());
  }

  private isNotEmpty(link: Link): boolean {
    return link && link.url && link.url.trim() !== '';
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty,
      true
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  isEmpty(): boolean {
    return !this.featureLinkValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  featureLinkValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    try {
      new URL(control.value);
    } catch (e) {
      return {featureLink: true};
    }

    return null;
  }

  private parseLinkData(link: Link): Link {
    if (!this.isNotEmpty(link)) {
      return null;
    }

    if (link.header !== null && link.header.trim() === '') {
      link.header = null;
    }

    return link;
  }

  get featureLinkValue(): any {
    return this.form.get('url');
  }
}
